/* eslint require-jsdoc: 'error', valid-jsdoc: ["error", { "requireReturn": false }] */

import AreaDataset from '@fusioncharts/charts/src/dataset/area';
import {
  hasSVG, getMouseCoordinate, mathAbs, pluck, pluckNumber, getValidValue, parseUnsafeString,
  parseTooltext, polyPathToPath, snapPoint
} from '@fusioncharts/core/src/lib';
import { _restore, _getJSONData, updateDataValue } from '../dragcolumn';
import dragAreaAnimation from './index.animation';
import { addDep } from '@fusioncharts/core/src/dependency-manager';

let SETROLLOVERATTR = 'setRolloverAttr',
  SETROLLOUTATTR = 'setRolloutAttr',
  ROLLOUT = 'DataPlotRollOut',
  ROLLOVER = 'DataPlotRollOver',
  DRAGLINE = 'dragLine',
  DRAGAREA = 'dragArea',
  DATAPLOTCLICK = 'dataplotclick',
  DEFAULT = 'default',
  dragMouseAttr = (hasSVG && 'ns-resize') || 'n-resize',
  mathMin = Math.min,
  mathMax = Math.max,
  isVML = false,
  _updateImage,
  _configure,
  __firePlotEvent,
  _drag,
  UNDEF;

addDep({
  name: 'dragAreaAnimation',
  type: 'animationRule',
  extension: dragAreaAnimation
});
/**
 * dataset class of dragcolumn inheriting from column class
 */
class DragAreaDataset extends AreaDataset {
  /**
   * Sets the type of the component
   * @return {string} type
   */
  getType () {
    return 'dataset';
  }
  /**
   * Sets the name of the component
   * @return {string} name
   */
  getName () {
    return 'dragArea';
  }
  /**
  * Function for parsing all the attributes and value given by the user at chart,dataset and set level.
  * This function is called once from the init() function of the Column class.
  * @param  {Object} datasetJSON JSON for dataset configurations
  */
  configureAttributes (datasetJSON) {
    var dataset = this,
      conf = dataset.config,
      chartAttr = dataset.getFromEnv('chart-attrib'),
      JSONData = conf.JSONData;

    conf.allowDrag = pluckNumber(JSONData.allowdrag, 1);
    conf.allowNegDrag = pluckNumber(JSONData.allownegativedrag, 1);
    conf.allowAxisChange = pluckNumber(chartAttr.allowaxischange, 1);
    conf.snapToDivOnly = pluckNumber(chartAttr.snaptodivonly, 0);
    conf.doNotSnap = pluckNumber(chartAttr.donotsnap, 0);
    conf.snapToDiv = pluckNumber(chartAttr.snaptodiv, 1);
    conf.snapToDivRelaxation = pluckNumber(chartAttr.snaptodivrelaxation, 10);
    if (conf.doNotSnap) {
      conf.snapToDiv = conf.snapToDivOnly = 0;
    }
    super.configureAttributes(datasetJSON);
  }
  /**
   * method to configure a particular data plot object
   * @param {number} i index of the data object to be configured
   * @param {Object} setData particular plot information in of i'th index in input json
   */
  _plotConfigure (i, setData) {
    let conf = this.config,
      dataObj,
      config;
    super._plotConfigure(i, setData);
    dataObj = this.components.data[i]; // eslint-disable-line good-practices/no-single-usage-variable
    config = dataObj.config;
    config.allowDrag = pluckNumber(setData.allowdrag, conf.allowDrag);
    config.allowNegDrag = pluckNumber(setData.allownegativedrag, conf.allowNegDrag);
  }

  /**
   * Function to update image while dragging.
   * @param {Object} dataObj The object by which the dataset is to be updated.
   */
  static updateImage (dataObj) {
    var graphics = dataObj.graphics,
      image = graphics.image || graphics.element,
      config = dataObj.config,
      anchorProps = config.anchorProps,
      hoverEffects = config.hoverEffects,
      imgRef = image && image.data('imgRef'),
      scale = anchorProps.imageScale,
      imgH = imgRef.height * scale * 0.01,
      imgW = imgRef.width * scale * 0.01,
      x = dataObj._xPos,
      y = dataObj._yPos,
      hoverScale = hoverEffects.imageHoverScale,
      hotW = (imgRef.width * hoverScale * 0.01), // eslint-disable-line good-practices/no-single-usage-variable
      hotH = (imgRef.height * hoverScale * 0.01), // eslint-disable-line good-practices/no-single-usage-variable
      markerRadius = anchorProps.radius = anchorProps.isAnchorRadius // eslint-disable-line good-practices/no-single-usage-variable
        ? anchorProps.radius : mathMin(imgW, imgH) / 2,
      imagePadding = anchorProps.imagePadding,
      rolloutClipRadius = markerRadius - imagePadding - anchorProps.borderThickness * 0.5,
      rolloverClipRadius = hoverEffects.radius -
        imagePadding - hoverEffects.anchorBorderThickness * 0.5,
      symbol = anchorProps.symbol[1],
      imageRollOverPath = polyPathToPath([symbol || 2, x, y, // eslint-disable-line good-practices/no-single-usage-variable
        rolloverClipRadius > 0 ? rolloverClipRadius : 0, hoverEffects.startAngle, hoverEffects.dip]),
      setRolloverAttr,
      imageRolloutPath = polyPathToPath([symbol || 2, x, y, // eslint-disable-line good-practices/no-single-usage-variable
        rolloutClipRadius > 0 ? rolloutClipRadius : 0, anchorProps.startAngle, 0]),
      setRolloutAttr = {
        x: x - imgRef.width * scale * 0.005,
        y: y - imgRef.height * scale * 0.005,
        width: imgW,
        height: imgH,
        alpha: 100
      };

    if (!isVML) {
      setRolloutAttr['clip-path'] = imageRolloutPath;
    }
    setRolloverAttr = {
      x: x - imgRef.width * hoverScale * 0.005,
      y: y - imgRef.height * hoverScale * 0.005,
      width: hotW,
      height: hotH,
      alpha: 100
    };

    if (!isVML) {
      setRolloverAttr['clip-path'] = imageRollOverPath;
    }
    image.attr(setRolloutAttr);
    image.data(SETROLLOVERATTR, setRolloverAttr);
    image.data(SETROLLOUTATTR, setRolloutAttr);
  }

  /**
  * Function called when drag is triggered over the datasets
  * @param {String} action dragstart/dragmove/dragend action
  * @param {*} oriEvent The original mouse event
  * @param {*} index the index of the data over which drag is triggered
  * @param {*} coordinateData the mouse-coordinate wrt to the chart container
  */
  drag (action, oriEvent, index, coordinateData) {
    let dataset = this,
      coordinate = coordinateData,
      datasetConf = dataset.config,
      datasetIndex = datasetConf.index,
      chart = dataset.getFromEnv('chart'),
      chartConfig = chart.config,
      canvasTop = chartConfig.canvasTop,
      plotItem,
      element,
      config,
      dataStore = dataset.components.data,
      value,
      formattedVal,
      yPos,
      rolloverdata,
      lowerDragBoundary,
      chartY,
      canvas = dataset.getFromEnv('paper').canvas, // eslint-disable-line good-practices/no-single-usage-variable
      style = canvas.style,
      yAxis = dataset.getFromEnv('yAxis'),
      ticks = yAxis.getTicks(),
      snapToDivRelaxation = mathAbs(yAxis.getValue(yAxis.getPixel(0) - datasetConf.snapToDivRelaxation)),
      snapPixel = datasetConf.snapToDivOnly
        ? (mathAbs(ticks[1] - ticks[0]) * 0.5) : snapToDivRelaxation,
      plotIndex,
      eventArgs,
      parserConfig = {
        xaxisName: chartConfig.xaxisname,
        yaxisName: chartConfig.yaxisname
      },
      anchorProps,
      datasetGraphics = dataset.getGraphicalElement(), // eslint-disable-line good-practices/no-single-usage-variable
      mainLineElement = datasetGraphics.lineElement,
      chartX,
      tolerance = chartConfig.dragTolerance + 1,
      setTooltext,
      toolText,
      allowDrag,
      xPos,
      rolloutdata,
      image,
      startIndex,
      endIndex,
      allowNegDrag,
      pathArr,
      anchorElement,
      anchorImageUrl,
      isHoverEnabled,
      hoverEffects,
      anchorStartAngle,
      eventArgsArr,
      hoverEnabled,
      datasetName = dataset.getName(),
      isDragLine = (datasetName === DRAGLINE) || (datasetName === DRAGAREA), // eslint-disable-line good-practices/no-single-usage-variable
      pointerMouseAttr = DEFAULT,
      macroIndices = [1, 2, 3, 4, 5, 6, 7], // eslint-disable-line good-practices/no-single-usage-variable
      JSONData = datasetConf.JSONData,
      chartContainer = dataset.getFromEnv('chart-container');

    coordinate = coordinate || getMouseCoordinate(chartContainer, oriEvent, chart);

    switch (action) {
      case 'dragstart':
        plotItem = dataStore[index];
        config = plotItem.config;
        yPos = plotItem._yPos;
        xPos = plotItem._xPos;
        allowDrag = config.allowDrag;
        chartY = coordinate.chartY;
        chartX = coordinate.chartX;
        if (allowDrag &&
          chartY >= yPos - tolerance && chartY <= yPos + tolerance &&
          chartX <= xPos + tolerance && chartX >= xPos - tolerance) {
          config.dragStart = true;
          config._pointerDy = 0;
          config._dragStartY = chartY;
          config._dragBuffer = yPos - chartY;
          plotItem.dragged = true;

          plotItem.startValue = config.setValue;
          plotItem.name = datasetConf.seriesname;
          plotItem.datasetIndex = datasetConf.index;
          anchorProps = config.anchorProps;
          hoverEffects = config.hoverEffects;
          eventArgs = {
            index: plotIndex + 1,
            dataIndex: plotIndex + 1,
            datasetIndex: plotItem.datasetIndex,
            startValue: plotItem.startValue,
            datasetName: plotItem.name,
            color: config.color,
            alpha: config.alpha,
            anchorBgColor: anchorProps.bgColor,
            anchorBgAlpha: anchorProps.anchorBgAlpha,
            anchorAlpha: anchorProps.anchorAlpha,
            anchorBorderColor: anchorProps.borderColor,
            anchorBorderThickness: anchorProps.borderThickness,
            anchorRadius: anchorProps.radius,
            anchorSides: anchorProps.sides,
            anchorStartAngle: anchorProps.startAngle,
            anchorHoverColor: hoverEffects.anchorColor,
            anchorHoverAlpha: hoverEffects.anchorAlpha,
            anchorHoverSides: hoverEffects.anchorSides,
            dashed: config.dashed,
            valuePosition: config.valuePosition,
            id: config.id
          };
          config.dragStart = true;
        } else {
          config.dragStart = false;
        }
        break;

      case 'dragmove':
        plotItem = dataStore[index];
        config = plotItem.config;
        if (config.dragStart) {
          chartY = coordinate.chartY;
          config.allowDrag && (style.cursor = dragMouseAttr);
          element = plotItem.graphics.element;
          config._pointerDy++;
          image = plotItem.graphics.image;
          xPos = plotItem._xPos;
          anchorElement = element;
          chartY += config._dragBuffer;
          allowNegDrag = config.allowNegDrag;
          lowerDragBoundary = allowNegDrag ? chartConfig.canvasBottom : yAxis.yBasePos;
          anchorProps = config.anchorProps;
          anchorStartAngle = anchorProps.startAngle || 90;
          hoverEffects = config.hoverEffects;
          isHoverEnabled = config.hoverEffects && config.hoverEffects.enabled;
          anchorImageUrl = anchorProps.imageUrl;
          if (chartY < canvasTop) {
            chartY = canvasTop;
          } else if (chartY > lowerDragBoundary) {
            chartY = lowerDragBoundary;
          }
          plotItem._yPos = yPos = chartY;
          config._y = value = config.setValue = yAxis.getValue(yPos);
          config._Py = yAxis.getPixel(config._y);
          formattedVal = dataset.getFromEnv('number-formatter').dataLabels(value);
          config.toolTipValue = formattedVal;
          config.displayValue = formattedVal;

          dataset.parseLabelAttributes(plotItem, plotIndex);
          dataset.drawLabel(plotIndex, plotIndex + 1);
          plotItem.graphics.element = element;
          dataset.getFromEnv('toolTipController').hide(datasetConf.currentToolTip);

          if (isVML && anchorImageUrl) {
            image = anchorElement;
          } else {
            if (isHoverEnabled && (rolloverdata = anchorElement.data(SETROLLOVERATTR))) {
              rolloverdata.path = polyPathToPath([hoverEffects.anchorSides || 2, xPos, yPos, hoverEffects.anchorRadius, hoverEffects.startAngle, hoverEffects.dip]);
            }
            if (isHoverEnabled && (rolloutdata = anchorElement.data(SETROLLOUTATTR))) {
              rolloutdata.path = polyPathToPath([anchorProps.symbol[1] || 2, xPos, yPos, anchorProps.radius, anchorProps.startAngle, config.dip || 0]);
            }

            anchorElement && anchorElement.attr(rolloutdata || {
              path: polyPathToPath([anchorProps.symbol[1] || 2, xPos, yPos,
                anchorProps.radius, anchorStartAngle, 0])
            });
          }

          if (image) {
            DragAreaDataset.updateImage(plotItem);
          }

          if (isDragLine) {
            dataset.drawCommonElements();
          }

          if (mainLineElement) {
            datasetConf = dataset.config;
            startIndex = config.pathStartIndex;
            endIndex = config.pathEndIndex;
            pathArr = config.lastPath;
            pathArr = dataset.getLinePath(dataStore, {
              begin: startIndex,
              end: endIndex
            });
            mainLineElement.attr({
              path: pathArr.getPathArr()
            });
          }

          if (config._pointerDy === 1) {
            eventArgs = {
              index: index,
              dataIndex: index,
              datasetIndex: datasetIndex,
              startValue: plotItem.startValue,
              datasetName: plotItem.name,
              color: config.color,
              alpha: config.alpha,
              anchorBgColor: anchorProps.bgColor,
              anchorBgAlpha: anchorProps.anchorBgAlpha,
              anchorAlpha: anchorProps.anchorAlpha,
              anchorBorderColor: anchorProps.borderColor,
              anchorBorderThickness: anchorProps.borderThickness,
              anchorRadius: anchorProps.radius,
              anchorSides: anchorProps.sides,
              anchorStartAngle: anchorProps.startAngle,
              anchorHoverColor: hoverEffects.anchorColor,
              anchorHoverAlpha: hoverEffects.anchorAlpha,
              anchorHoverSides: hoverEffects.anchorSides,
              dashed: config.dashed,
              valuePosition: config.valuePosition,
              id: config.id
            };
            chart.fireChartInstanceEvent('dataplotDragStart', eventArgs);
          }
        }
        break;

      case 'dragend':
        plotItem = dataStore[index];
        config = plotItem.config;
        if (config.dragStart) {
          dataset.setMaxMin(plotItem);
          if (datasetConf.snapToDiv || datasetConf.snapToDivOnly) {
            element = plotItem.graphics.element;
            image = plotItem.graphics.image;
            xPos = plotItem._xPos;
            anchorElement = element;
            anchorProps = config.anchorProps;
            anchorStartAngle = anchorProps.startAngle || 90;
            hoverEffects = config.hoverEffects;
            isHoverEnabled = config.hoverEffects && config.hoverEffects.enabled;
            anchorImageUrl = anchorProps.imageUrl;
            config.setValue = snapPoint({ snapPixel, datasetConf }, ticks, plotItem);
            chartY = yAxis.getPixel(config.setValue);
            plotItem._yPos = yPos = chartY;
            config._y = value = config.setValue;
            config._Py = yAxis.getPixel(config._y);
            formattedVal = dataset.getFromEnv('number-formatter').dataLabels(value);
            config.toolTipValue = formattedVal;
            config.displayValue = formattedVal;
            dataset.parseLabelAttributes(plotItem, plotIndex);
            dataset.drawLabel(plotIndex, plotIndex + 1);
            plotItem.graphics.element = element;
            // toolTipController.hide(datasetConf.currentToolTip);
            if (isVML && anchorImageUrl) {
              image = anchorElement;
            } else {
              if (isHoverEnabled && (rolloverdata = anchorElement.data(SETROLLOVERATTR))) {
                rolloverdata.path = polyPathToPath([hoverEffects.anchorSides || 2, xPos, yPos, hoverEffects.anchorRadius, hoverEffects.startAngle, hoverEffects.dip]);
              }
              if (isHoverEnabled && (rolloutdata = anchorElement.data(SETROLLOUTATTR))) {
                rolloutdata.path = polyPathToPath([anchorProps.symbol[1] || 2, xPos, yPos, anchorProps.radius, anchorProps.startAngle, config.dip || 0]);
              }

              anchorElement && anchorElement.attr(rolloutdata || {
                path: polyPathToPath([anchorProps.symbol[1] || 2, xPos, yPos,
                  anchorProps.radius, anchorStartAngle, 0])
              });
            }
            if (image) {
              DragAreaDataset.updateImage(plotItem);
            }

            if (isDragLine) {
              dataset.drawCommonElements();
            }
            if (mainLineElement) {
              datasetConf = dataset.config;
              startIndex = config.pathStartIndex;
              endIndex = config.pathEndIndex;
              pathArr = config.lastPath;
              pathArr = dataset.getLinePath(dataStore, {
                begin: startIndex,
                end: endIndex
              });
              mainLineElement.attr({
                path: pathArr.getPathArr()
              });
            }
          }
          eventArgs = {
            index: index,
            dataIndex: index,
            datasetIndex: datasetIndex,
            startValue: plotItem.startValue,
            endValue: config.setValue,
            datasetName: plotItem.name,
            color: config.color,
            alpha: config.alpha,
            anchorBgColor: anchorProps.bgColor,
            anchorBgAlpha: anchorProps.anchorBgAlpha,
            anchorAlpha: anchorProps.anchorAlpha,
            anchorBorderColor: anchorProps.borderColor,
            anchorBorderThickness: anchorProps.borderThickness,
            anchorRadius: anchorProps.radius,
            anchorSides: anchorProps.sides,
            anchorStartAngle: anchorProps.startAngle,
            anchorHoverColor: hoverEffects.anchorColor,
            anchorHoverAlpha: hoverEffects.anchorAlpha,
            anchorHoverSides: hoverEffects.anchorSides,
            dashed: config.dashed,
            valuePosition: config.valuePosition,
            id: config.id
          };
          eventArgsArr = [
            chart.getFromEnv('chartInstance').id,
            eventArgs.dataIndex,
            eventArgs.datasetIndex,
            eventArgs.datasetName,
            eventArgs.startValue,
            eventArgs.endValue
          ];

          element && updateDataValue.call(element, oriEvent, chart); // Update dataValue
          if (config._pointerDy) {
            hoverEnabled && dataset._hoverPlotAnchor(plotItem, ROLLOUT);
            chart.fireChartInstanceEvent('dataplotDragEnd', eventArgs);
            // Fire the ChartUpdated event
            chart.fireChartInstanceEvent('chartupdated', eventArgs, eventArgsArr);
          }

          setTooltext = getValidValue(parseUnsafeString(pluck(plotItem.tooltext,
            JSONData.plottooltext, dataset.getFromEnv('chart-attrib').plottooltext), false));

          if (setTooltext !== UNDEF) {
            parserConfig.formattedValue = config.toolTipValue;
            parserConfig.label = config.label;
            toolText = parseTooltext(setTooltext, macroIndices,
              parserConfig, { value: config.toolTipValue }, UNDEF, JSONData);
            config.setTooltext = toolText;
            setTooltext = toolText;
            config.toolText = toolText;
          }
          toolText = config.finalTooltext = config.toolText !== false ? (
            (setTooltext ||
              config.toolText.substring(0, config.toolText.indexOf(config.formatedVal)) +
              config.toolTipValue)) : '';

          if (!(chartY >= yPos - tolerance && chartY <= yPos + tolerance &&
            chartX <= xPos + tolerance && chartX >= xPos - tolerance)) {
            style.cursor = pointerMouseAttr;
          }
          config._dragBuffer = 0;
          config._pointerDy = 0;
          config.dragStart = false;
        }
        break;
      default: break;
    }
  }
  /**
   * This method handles all mouse events of an dataset.
   * @param {string} eventType name of the event
   * @param {number} plotIndex index of the plot where this event has been occured
   * @param {Event} e reference of the original mouse event
   */
  _firePlotEvent (eventType, plotIndex, e) {
    var dataset = this,
      dataSetConf = dataset.config,
      chart = dataset.getFromEnv('chart'),
      chartConfig = dataset.getFromEnv('chartConfig'),
      drawTrendRegion = chartConfig.drawTrendRegion,
      // chartComp = chart.components,
      paper = dataset.getFromEnv('paper'),
      toolTipController = dataset.getFromEnv('toolTipController'),
      canvas = paper.canvas,
      style = canvas.style,
      datasetComp = dataset.components,
      plotItem = datasetComp.data[plotIndex],
      currentToolTip = dataSetConf.currentToolTip,
      originalEvent = e.originalEvent,
      pointerMouseAttr = DEFAULT,
      tolerance,
      coordinate,
      chartY,
      chartX,
      yPos,
      xPos,
      element,
      hoverEnabled,
      toolText,
      eventArgs,
      config,
      changedTouch,
      anchorProps;

    if (eventType === 'touchend') {
      changedTouch = originalEvent.changedTouches[0];
      originalEvent.pageX = changedTouch && changedTouch.pageX;
      originalEvent.pageY = changedTouch && changedTouch.pageY;
    }
    coordinate = getMouseCoordinate(chart.getFromEnv('chart-container'), originalEvent, chart);
    chartY = coordinate.chartY;
    chartX = coordinate.chartX;

    if (plotItem) {
      element = plotItem.graphics.element;
      config = plotItem.config;
      anchorProps = config.anchorProps;
      toolText = config.finalTooltext;
      hoverEnabled = config.hoverEffects.enabled;
      eventArgs = config.eventArgs;
      yPos = plotItem._yPos;
      xPos = plotItem._xPos;
      config.dragTolerance = config.dragTolerance < anchorProps.markerRadius
        ? anchorProps.markerRadius + 0.5 : config.dragTolerance;
      tolerance = mathMax(config.dragTolerance, config.hoverEffects.anchorRadius || 0) + 1;

      switch (eventType) {
        case 'fc-mouseover':
          if (config.allowDrag) {
            style.cursor = dragMouseAttr;
          }
          if (!config.dragStart && toolText && !config.dragStart && !drawTrendRegion) {
            if (currentToolTip) {
              toolTipController.draw(originalEvent, toolText, currentToolTip);
            } else {
              currentToolTip = dataSetConf.currentToolTip = toolTipController.draw(originalEvent, toolText);
            }
          }
          if (!config.dragStart) {
            hoverEnabled && dataset._hoverPlotAnchor(plotItem, ROLLOVER);
            element && chart.plotEventHandler(element, e, ROLLOVER, eventArgs);
          }
          break;

        case 'fc-mouseout':
          style.cursor = pointerMouseAttr;

          hoverEnabled && dataset._hoverPlotAnchor(plotItem, ROLLOUT);
          element && chart.plotEventHandler(element, e, ROLLOUT, eventArgs);
          toolTipController.hide(dataSetConf.currentToolTip);
          break;

        case 'fc-mousemove':
          if (!config.dragStart && toolText &&
            chartY >= yPos - tolerance && chartY <= yPos + tolerance &&
            chartX <= xPos + tolerance && chartX >= xPos - tolerance) {
            config.allowDrag && (style.cursor = dragMouseAttr);
            if (currentToolTip) {
              toolTipController.draw(originalEvent, toolText, currentToolTip);
            } else {
              currentToolTip = dataSetConf.currentToolTip = toolTipController.draw(originalEvent, toolText);
            }
          } else {
            toolTipController.hide(dataSetConf.currentToolTip);
          }

          break;

        case 'fc-click':
          element && chart.plotEventHandler(element, e, DATAPLOTCLICK, eventArgs);
          break;
        default: break;
      }
    }
  }

  /**
   * Function to restore the values of the dataset to its original
   */
  restore () {
    _restore.call(this);
  }

  /**
   * Fucntion to get the updated values of data when they are dragged.
   * @return {Object} The corresponding data Array of the dataset containing the values of all the sets.
   */
  getJSONData () {
    return _getJSONData.call(this);
  }
}
_updateImage = DragAreaDataset.prototype.updateImage;
__firePlotEvent = DragAreaDataset.prototype._firePlotEvent;
_configure = DragAreaDataset.prototype.configureAttributes;
_drag = DragAreaDataset.prototype.drag;

export { __firePlotEvent, _configure as configurer, _updateImage, _drag };
export default DragAreaDataset;
